/**
 * useContractReferenceData Hook
 * Loads all reference data needed for contract forms
 */

import { useEffect, useCallback } from 'react';
import { apiGet, extractApiData } from '../utils/apiClient';
import { useContractFormContext } from '../components/contracts/wizard/ContractFormContext';
import logger from '../utils/logger';

export function useContractReferenceData() {
  const {
    setRefData,
    setRefDataLoading,
    setRefDataError,
    refData,
    refDataLoading,
    refDataError,
  } = useContractFormContext();

  const loadReferenceData = useCallback(async () => {
    try {
      setRefDataLoading(true);
      setRefDataError(null);

      const [
        investorsData,
        productsData,
        frequenciesData,
        interestTypesData,
        interestRatesData,
        placementFeesData,
        brokersData,
        providersData,
        issuersData,
        collateralTypesData,
        currenciesData,
        calculationProfilesData,
        commissionRatesData,
      ] = await Promise.all([
        apiGet('/stakeholders/investors/select'),
        apiGet('/refdata/investment-types'),
        apiGet('/refdata/frequencies'),
        apiGet('/refdata/interest-types?active=true'),
        apiGet('/refdata/interest-rates'),
        apiGet('/refdata/placement-fees'),
        apiGet('/stakeholders/brokers/select'),
        apiGet('/stakeholders/collateral-providers/select'),
        apiGet('/stakeholders/issuers/select').catch(() => ({ data: [] })),
        apiGet('/refdata/collateral-types'),
        apiGet('/refdata/countries'),
        apiGet('/refdata/calculation-profiles'),
        apiGet('/refdata/commission-rates'),
      ]);

      setRefData({
        investors: extractApiData(investorsData),
        products: extractApiData(productsData),
        frequencies: extractApiData(frequenciesData),
        interestTypes: extractApiData(interestTypesData),
        interestRates: extractApiData(interestRatesData),
        placementFees: extractApiData(placementFeesData),
        brokers: extractApiData(brokersData),
        collateralProviders: extractApiData(providersData),
        issuers: extractApiData(issuersData),
        collateralTypes: extractApiData(collateralTypesData),
        currencies: extractApiData(currenciesData),
        calculationProfiles: extractApiData(calculationProfilesData),
        commissionRates: extractApiData(commissionRatesData),
      });
    } catch (err) {
      logger.error('Error loading reference data', { message: err.message });
      setRefDataError('Failed to load reference data. Please refresh the page or contact support.');
    } finally {
      setRefDataLoading(false);
    }
  }, [setRefData, setRefDataLoading, setRefDataError]);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  return {
    ...refData,
    loading: refDataLoading,
    error: refDataError,
    reload: loadReferenceData,
  };
}

export default useContractReferenceData;
